import api from './api';

export interface UsageQuery {
  startDate: string;
  endDate: string;
  clientIds?: number[];
  includeEmpty?: boolean;
}

export interface DateRangeInfo {
  start: string;
  end: string;
  days: number;
}

export interface UsageFilters {
  startDate: string;
  endDate: string;
  clientIds?: number[];
  includeEmpty: boolean;
}

export interface ClientMetaInfo {
  id: number;
  username: string;
  email: string;
  status: string;
  createdAt: string;
}

export interface DailyUsageRecord {
  clientId: number;
  clientName: string;
  date: string;
  totalVisits: number;
  billableEvents: number;
  normalVisits: number;
  interceptedVisits: number;
  interceptRate: number;
  ipcoreUsage: number;
  fingerprintUsage: number;
}

export interface ClientSummaryRecord {
  clientId: number;
  clientName: string;
  email: string;
  status: string;
  createdAt: string;
  totalVisits: number;
  billableEvents: number;
  normalVisits: number;
  interceptedVisits: number;
  activeDays: number;
  days: number;
  averageDailyVisits: number;
  interceptRate: number;
  ipcoreUsage: number;
  fingerprintUsage: number;
}

export interface DailyTotalRecord {
  date: string;
  totalVisits: number;
  billableEvents: number;
  normalVisits: number;
  interceptedVisits: number;
}

export interface UsageTotals {
  clients: number;
  activeClients: number;
  days: number;
  totalVisits: number;
  billableEvents: number;
  normalVisits: number;
  interceptedVisits: number;
  avgPerClientPerDay: number;
  avgInterceptRate: number;
  avgBillablePerClient: number;
}

export interface UsageMeta {
  generatedAt: string;
  nonZeroRows: number;
  queriedClients?: number[];
}

export interface UsageResponse {
  dateRange: DateRangeInfo;
  filters: UsageFilters;
  clientMeta: ClientMetaInfo[];
  clientSummary: ClientSummaryRecord[];
  dailyUsage: DailyUsageRecord[];
  dailyTotals: DailyTotalRecord[];
  totals: UsageTotals;
  meta: UsageMeta;
}

const toNumber = (value: any, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toStringValue = (value: any, fallback = ''): string => {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return fallback;
  return String(value);
};

const toBoolean = (value: any, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

export const usageService = {
  async getDailyUsage(params: UsageQuery): Promise<UsageResponse> {
    const response = await api.post('/api/usage/daily', {
      start_date: params.startDate,
      end_date: params.endDate,
      client_ids: params.clientIds,
      include_empty: params.includeEmpty,
    });

    const payload = response?.data?.data ?? {};

    const dateRangeRaw = payload.date_range ?? {};
    const filtersRaw = payload.filters ?? {};

    const dateRange: DateRangeInfo = {
      start: toStringValue(dateRangeRaw.start),
      end: toStringValue(dateRangeRaw.end),
      days: toNumber(dateRangeRaw.days),
    };

    const filters: UsageFilters = {
      startDate: toStringValue(filtersRaw.start_date, dateRange.start),
      endDate: toStringValue(filtersRaw.end_date, dateRange.end),
      clientIds: Array.isArray(filtersRaw.client_ids)
        ? filtersRaw.client_ids.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id))
        : undefined,
      includeEmpty: toBoolean(filtersRaw.include_empty, true),
    };

    const clientMeta: ClientMetaInfo[] = Array.isArray(payload.client_meta)
      ? payload.client_meta.map((item: any) => ({
          id: Number(item.id) || 0,
          username: toStringValue(item.username),
          email: toStringValue(item.email),
          status: toStringValue(item.status),
          createdAt: toStringValue(item.created_at),
        }))
      : [];

    const clientSummary: ClientSummaryRecord[] = Array.isArray(payload.client_summary)
      ? payload.client_summary.map((item: any) => ({
          clientId: Number(item.client_id) || 0,
          clientName: toStringValue(item.client_name),
          email: toStringValue(item.email),
          status: toStringValue(item.status),
          createdAt: toStringValue(item.created_at),
          totalVisits: toNumber(item.total_visits),
          billableEvents: toNumber(item.billable_events),
          normalVisits: toNumber(item.normal_visits),
          interceptedVisits: toNumber(item.intercepted_visits),
          activeDays: toNumber(item.active_days),
          days: toNumber(item.days),
          averageDailyVisits: toNumber(item.average_daily_visits),
          interceptRate: toNumber(item.intercept_rate),
          ipcoreUsage: toNumber(item.ipcore_usage),
          fingerprintUsage: toNumber(item.fingerprint_usage),
        }))
      : [];

    const dailyUsage: DailyUsageRecord[] = Array.isArray(payload.daily_usage)
      ? payload.daily_usage.map((item: any) => ({
          clientId: Number(item.client_id) || 0,
          clientName: toStringValue(item.client_name),
          date: toStringValue(item.date),
          totalVisits: toNumber(item.total_visits),
          billableEvents: toNumber(item.billable_events),
          normalVisits: toNumber(item.normal_visits),
          interceptedVisits: toNumber(item.intercepted_visits),
          interceptRate: toNumber(item.intercept_rate),
          ipcoreUsage: toNumber(item.ipcore_usage),
          fingerprintUsage: toNumber(item.fingerprint_usage),
        }))
      : [];

    const dailyTotals: DailyTotalRecord[] = Array.isArray(payload.daily_totals)
      ? payload.daily_totals.map((item: any) => ({
          date: toStringValue(item.date),
          totalVisits: toNumber(item.total_visits),
          billableEvents: toNumber(item.billable_events),
          normalVisits: toNumber(item.normal_visits),
          interceptedVisits: toNumber(item.intercepted_visits),
        }))
      : [];

    const totalsRaw = payload.totals ?? {};
    const totals: UsageTotals = {
      clients: toNumber(totalsRaw.clients),
      activeClients: toNumber(totalsRaw.active_clients),
      days: toNumber(totalsRaw.days),
      totalVisits: toNumber(totalsRaw.total_visits),
      billableEvents: toNumber(totalsRaw.billable_events),
      normalVisits: toNumber(totalsRaw.normal_visits),
      interceptedVisits: toNumber(totalsRaw.intercepted_visits),
      avgPerClientPerDay: toNumber(totalsRaw.avg_per_client_per_day),
      avgInterceptRate: toNumber(totalsRaw.avg_intercept_rate),
      avgBillablePerClient: toNumber(totalsRaw.avg_billable_per_client),
    };

    const metaRaw = payload.meta ?? {};
    const meta: UsageMeta = {
      generatedAt: toStringValue(metaRaw.generated_at),
      nonZeroRows: toNumber(metaRaw.non_zero_rows),
      queriedClients: Array.isArray(metaRaw.queried_clients)
        ? metaRaw.queried_clients.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id))
        : undefined,
    };

    return {
      dateRange,
      filters,
      clientMeta,
      clientSummary,
      dailyUsage,
      dailyTotals,
      totals,
      meta,
    };
  },
};

export default usageService;

